// src/app/components/reporting/components/insights-list.tsx
import { Loader2, Bookmark, CheckCircle2, Sparkles } from 'lucide-react';
import { InsightCard } from './insights-card';
import type { SavedInsight } from '../types';

interface InsightsListProps {
  insights: SavedInsight[]; isLoading: boolean; selectedIds: number[];
  onToggleSelect: (id: number) => void; onSelectAll: () => void;
  onClearAll: () => void; onSelectAiRecommended: () => void; onDelete: (id: number) => void;
}

export function InsightsList({
  insights, isLoading, selectedIds,
  onToggleSelect, onSelectAll, onClearAll, onSelectAiRecommended, onDelete,
}: InsightsListProps) {
  if (isLoading) return (
    <div className="flex items-center justify-center p-10 md:p-12">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--muted-foreground)' }} />
    </div>
  );

  if (!insights.length) return (
    <div
      className="flex flex-col items-center justify-center p-10 md:p-12 text-center"
      style={{
        backgroundColor: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '16px',
      }}
    >
      <Bookmark className="w-10 h-10 mb-3" style={{ color: 'var(--muted-foreground)' }} />
      <p style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: '6px' }}>
        No insights saved yet
      </p>
      <p
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--muted-foreground)',
          lineHeight: '1.6',
          maxWidth: '260px',
        }}
      >
        Use the "Save to Insights" button from Antenna, Cognition, Analytics, or Fix Actions.
      </p>
    </div>
  );

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: 'var(--card)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--elevation-sm)',
      }}
    >
      <div
        className="p-4 md:p-6"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <h3
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            marginBottom: '2px',
          }}
        >
          Report Composer
        </h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
          Select insights to include in your report
        </p>
      </div>

      <div
        className="p-3 md:p-4"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        {/* Label row — stacks on mobile (title/desc block, then action pills) */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h4
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)',
                marginBottom: '2px',
              }}
            >
              Saved Insights ({insights.length})
            </h4>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              Click to expand and review
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedIds.length > 0 && (
              <button
                onClick={onClearAll}
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  textDecoration: 'underline',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Clear All
              </button>
            )}
            <button
              onClick={onSelectAiRecommended}
              className="px-2.5 py-1.5 rounded-lg flex items-center gap-1.5"
              style={{
                backgroundColor: 'var(--spectral-violet-5)',
                border: '1px solid var(--spectral-violet-12)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--spectral-violet)',
                cursor: 'pointer',
              }}
            >
              <Sparkles className="w-3.5 h-3.5" /> AI Pick
            </button>
            <button
              onClick={onSelectAll}
              className="px-2.5 py-1.5 rounded-lg"
              style={{
                backgroundColor: 'var(--muted)',
                border: '1px solid var(--border)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
              }}
            >
              Select All
            </button>
          </div>
        </div>
        {selectedIds.length > 0 && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{
              backgroundColor: 'var(--alpha-teal-5)',
              border: '1px solid var(--alpha-teal-12)',
            }}
          >
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--alpha-teal)' }} />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
              }}
            >
              {selectedIds.length} insight{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
          </div>
        )}
      </div>

      <div className="max-h-[600px] overflow-y-auto">
        {insights.map((insight) => (
          <InsightCard
            key={insight.id}
            insight={insight}
            isSelected={selectedIds.includes(insight.id)}
            onToggleSelect={onToggleSelect}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}